import { useCallback, useEffect, useState } from "react";
import { defaultLocalStorage } from "./usePersistedState.js";
import {
  LEGACY_CAPTURE_COOKIE,
  captureOptInCookieToken,
  captureOptInToken,
  isCaptureDefaultOn,
  migrateLegacyChoices,
  readCaptureCookie,
  readStoredChoices,
  resolveCaptureOptIn,
  setCaptureChoice,
  syncCaptureOptInCookie,
  withLegacyChoices,
} from "../lib/responseCaptureOptIn.js";

/**
 * The cookie's choices at mount, after merging an earlier build's localStorage choices into it
 * and re-setting it (its lifetime restarts). Idempotent, so a repeated call (and every later
 * load, which merges the legacy copy again) changes nothing.
 *
 * @returns {string}
 */
function initialChoices() {
  const storage = defaultLocalStorage();
  // An earlier build's stored choice and cookie (where they disagree the opt-out wins) fill in
  // the accounts the choices cookie has no choice for.
  const legacy = migrateLegacyChoices(
    readStoredChoices(storage),
    readCaptureCookie(globalThis.document, LEGACY_CAPTURE_COOKIE),
  );
  const merged = withLegacyChoices(readCaptureCookie(), legacy);
  syncCaptureOptInCookie(captureOptInCookieToken(merged));
  // The legacy copy stays: removing it would send a storage event to a tab still running an
  // earlier build, whose cookie sync would then rewrite that build's cookie.
  return readCaptureCookie() ?? "";
}

/**
 * The Settings > Debug "Capture responses for replay" toggle: on by default for a
 * DEFAULT_ON_EMAIL_DOMAINS account, off for any other. The cookie holds one choice per account
 * that set it (their token for on, their opt-out for off); each applies only while that same
 * user is signed in, and an account without one sees its default. The cookie is the only
 * store, shared by every tab: a change reads it afresh (setCaptureChoice), so a tab that has
 * not yet seen another tab's change never writes it away. The toggle re-reads it when the tab
 * regains focus or becomes visible. On mount, choices an earlier build kept in localStorage
 * are merged into the cookie, and the cookie is re-set so its lifetime restarts.
 *
 * @param {{sub: string|null|undefined, email: string|null|undefined}} deps  the signed-in
 *   user's sub and email (`me?.sub`, `me?.email`)
 * @returns {{responseCaptureOptIn: boolean, setResponseCaptureOptIn: Function}}
 */
export function useResponseCaptureOptIn({ sub, email }) {
  const [choices, setChoices] = useState(initialChoices);
  // Another tab's change: shown once this tab is looked at again.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const reread = () => setChoices(readCaptureCookie() ?? "");
    window.addEventListener("focus", reread);
    document.addEventListener("visibilitychange", reread);
    return () => {
      window.removeEventListener("focus", reread);
      document.removeEventListener("visibilitychange", reread);
    };
  }, []);
  const [computed, setComputed] = useState({ sub: null, token: null });
  useEffect(() => {
    let live = true;
    captureOptInToken(sub).then((token) => {
      if (live) setComputed({ sub: sub || null, token });
    });
    return () => {
      live = false;
    };
  }, [sub]);
  const { optIn: responseCaptureOptIn, token } = resolveCaptureOptIn({
    storedToken: choices,
    computed,
    sub,
    defaultOn: isCaptureDefaultOn(email),
  });
  const setResponseCaptureOptIn = useCallback(
    (next) => {
      const on = (typeof next === "function" ? next(responseCaptureOptIn) : next) === true;
      setChoices(setCaptureChoice(on, token));
    },
    [responseCaptureOptIn, token],
  );
  return { responseCaptureOptIn, setResponseCaptureOptIn };
}
